import { useState, useEffect } from 'react';

export default function Insights() {
  const [summary, setSummary] = useState({});
  const [byNiche, setByNiche] = useState([]);
  const [miningByDay, setMiningByDay] = useState([]);
  const [topOffers, setTopOffers] = useState([]);
  const [hoverBar, setHoverBar] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/insights/summary').then(r => r.json()).catch(() => ({})),
      fetch('/api/insights/by-niche?period=30d').then(r => r.json()).catch(() => ({ data: [] })),
      fetch('/api/insights/mining-by-day?period=14d').then(r => r.json()).catch(() => ({ data: [] })),
      fetch('/api/insights/top-offers?limit=10').then(r => r.json()).catch(() => ({ data: [] })),
    ]).then(([sum, niche, mining, offers]) => {
      setSummary(sum);
      setByNiche(niche.data || []);
      setMiningByDay(mining.data || []);
      setTopOffers(offers.data || []);
      setLoading(false);
    });
  }, []);

  const fmt = (n) => n != null ? n.toLocaleString() : '0';
  const maxCount = Math.max(...miningByDay.map(d => d.count), 1);
  const maxNiche = byNiche.length > 0 ? byNiche[0].count : 1;
  const nicheColors = ['var(--accent)', 'var(--accent-g)', 'var(--accent-p)', 'var(--accent-a)', 'var(--accent-r)'];

  if (loading) return <div style={s.page}><div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>Carregando...</div></div>;

  return (
    <div style={s.page}>
      {/* KPIs */}
      <div style={s.kpiRow}>
        <Kpi label="Hoje" value={fmt(summary.today)} color="var(--accent)" />
        <Kpi label="Semana" value={fmt(summary.week)} color="var(--accent-g)" />
        <Kpi label="Total" value={fmt(summary.total)} color="var(--accent-p)" />
        <Kpi label="Ofertas" value={fmt(summary.offers)} color="var(--accent-a)" />
        <Kpi label="Cloakers" value={fmt(summary.cloakers)} color="var(--accent-r)" />
      </div>

      <div style={s.grid2}>
        {/* Mining Chart */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>Mineracao por dia</span>
            <span style={s.cardSub}>14 dias</span>
          </div>
          <div style={s.chart}>
            {miningByDay.map((d, i) => (
              <div key={i} style={s.chartCol}
                onMouseEnter={() => setHoverBar(i)} onMouseLeave={() => setHoverBar(null)}>
                {hoverBar === i && <div style={s.tooltip}>{d.count} ads</div>}
                <div style={s.chartBarWrap}>
                  <div style={{
                    ...s.chartBar,
                    height: `${Math.max((d.count / maxCount) * 100, 3)}%`,
                    background: hoverBar === i ? 'var(--accent)' : 'var(--accent-p)',
                    opacity: hoverBar === i ? 1 : 0.6,
                  }} />
                </div>
                <span style={s.chartLabel}>{d.date.slice(8)}/{d.date.slice(5, 7)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* By Niche */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>Por nicho</span>
            <span style={s.cardSub}>30 dias</span>
          </div>
          <div style={s.nicheList}>
            {byNiche.map((n, i) => (
              <div key={n.nicho} style={s.nicheRow}>
                <span style={s.nicheName}>{n.nicho}</span>
                <div style={s.nicheBar}>
                  <div style={{
                    ...s.nicheBarFill,
                    width: `${(n.count / maxNiche) * 100}%`,
                    background: nicheColors[i % nicheColors.length],
                  }} />
                </div>
                <span style={s.nicheCount}>{n.count}</span>
              </div>
            ))}
            {byNiche.length === 0 && <div style={s.empty}>Sem dados</div>}
          </div>
        </div>
      </div>

      {/* Top Offers */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <span style={s.cardTitle}>Top ofertas</span>
          <span style={s.cardSub}>Por ads minerados</span>
        </div>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Dominio</th>
                <th style={s.th}>Nicho</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Ads Minerados</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Ad Library</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Pagina</th>
              </tr>
            </thead>
            <tbody>
              {topOffers.map((o, i) => {
                const stColor = { escalando: 'var(--accent-g)', estavel: 'var(--accent)', caindo: 'var(--accent-r)', morto: 'var(--text3)' };
                return (
                  <tr key={i}>
                    <td style={s.td}><span style={{ color: 'var(--accent)', fontWeight: 600 }}>{o.domain}</span></td>
                    <td style={s.td}>{o.nicho && <span style={s.nicheBadge}>{o.nicho}</span>}</td>
                    <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{o.ad_count}</td>
                    <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{o.adlib_count ? fmt(o.adlib_count) : '—'}</td>
                    <td style={s.td}>
                      <span style={{ ...s.statusBadge, color: stColor[o.status] || 'var(--text3)' }}>
                        {o.status || 'novo'}
                      </span>
                    </td>
                    <td style={{ ...s.td, color: 'var(--text3)', fontSize: 12 }}>{o.sample_page || '—'}</td>
                  </tr>
                );
              })}
              {topOffers.length === 0 && (
                <tr><td colSpan={6} style={s.empty}>Sem dados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div style={{ ...s.kpi, borderLeft: `3px solid ${color}` }}>
      <div style={s.kpiLabel}>{label}</div>
      <div style={s.kpiValue}>{value}</div>
    </div>
  );
}

const s = {
  page: { padding: '24px', display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1200, margin: '0 auto' },

  // KPIs
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 },
  kpi: { background: 'var(--surface)', borderRadius: 8, padding: '14px 16px', border: '1px solid var(--border)' },
  kpiLabel: { fontSize: 10, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  kpiValue: { fontSize: 16, fontWeight: 700, color: 'var(--text)', marginTop: 4 },

  // Grid
  grid2: { display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16 },

  // Card
  card: { background: 'var(--surface)', borderRadius: 8, padding: '16px 20px', border: '1px solid var(--border)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  cardTitle: { fontSize: 13, fontWeight: 600, color: 'var(--text)' },
  cardSub: { fontSize: 11, color: 'var(--text3)' },

  // Chart
  chart: { display: 'flex', gap: 3, alignItems: 'flex-end', height: 180, position: 'relative' },
  chartCol: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer', position: 'relative' },
  chartBarWrap: { flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  chartBar: { width: '65%', borderRadius: '4px 4px 0 0', minHeight: 3, transition: 'all 0.2s' },
  chartLabel: { fontSize: 10, color: 'var(--text3)', whiteSpace: 'nowrap' },
  tooltip: {
    position: 'absolute', top: -24, left: '50%', transform: 'translateX(-50%)',
    padding: '2px 8px', borderRadius: 4, background: 'var(--text)', color: 'var(--bg)',
    fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', zIndex: 10,
  },

  // Niche
  nicheList: { display: 'flex', flexDirection: 'column', gap: 10 },
  nicheRow: { display: 'flex', alignItems: 'center', gap: 10 },
  nicheName: { fontSize: 11, fontWeight: 500, minWidth: 90, textTransform: 'capitalize', color: 'var(--text2)' },
  nicheBar: { flex: 1, height: 10, background: 'var(--surface3)', borderRadius: 5, overflow: 'hidden' },
  nicheBarFill: { height: '100%', borderRadius: 5, transition: 'width 0.4s ease' },
  nicheCount: { fontSize: 11, fontWeight: 700, color: 'var(--text)', minWidth: 30, textAlign: 'right' },

  // Table
  tableWrap: { overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--border)', fontWeight: 600 },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--surface3)' },
  nicheBadge: { padding: '2px 8px', background: 'rgba(167,139,250,0.1)', color: 'var(--accent-p)', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  statusBadge: { fontSize: 11, fontWeight: 600, textTransform: 'capitalize' },
  empty: { padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 12 },
};
